import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

// ---------------------------------------------------------------------------
// OPUS-1172: event-sourced clock model. The clock is derived from the fixture's
// clock_* events (durable, fixture-scoped) instead of the transient broker
// state, and ticks against DDS server time (getServerNow) — never the
// operator's system clock. Pure helpers below so the derivation is testable and
// closure-free.
// ---------------------------------------------------------------------------
const CLOCK_EVENT_KEYS = {
    START_1H: "clock_start_1h",
    END_1H: "clock_end_1h",
    START_2H: "clock_start_2h",
    END_2H: "clock_end_2h",
    RESET: "clock_match_reset",
    ADDITIONAL: "additional_time",
    OVERWRITE: "clock_overwrite",
};

const keyOf = (e) => e?.event_key || e?.event_type || null;

// Resolve an event's reference instant (ms since epoch). Prefer the
// server-stamped ref_clock (OPUS-1172); fall back to the epoch baked into
// event_id (`<key>_<epoch>`), then updatedAt — so the clock still works for
// events written before ref_clock existed.
const eventInstantMs = (e) => {
    if (!e) return 0;
    if (e.ref_clock) {
        const t = Date.parse(e.ref_clock);
        if (!Number.isNaN(t)) return t;
    }
    const m = String(e.event_id ?? "").match(/_(\d{10,})$/);
    if (m) return Number(m[1]);
    if (e.updatedAt) {
        const t = Date.parse(e.updatedAt);
        if (!Number.isNaN(t)) return t;
    }
    return 0;
};

// Additional-time minutes announced for a period → seconds. event_name carries
// the minutes; use the latest additional_time for that period.
const additionalSecondsForPeriod = (events, period) => {
    const adds = events
        .filter((e) => keyOf(e) === CLOCK_EVENT_KEYS.ADDITIONAL && Number(e.game_period) === period)
        .sort((a, b) => eventInstantMs(b) - eventInstantMs(a));
    if (!adds.length) return 0;
    const mins = parseInt(adds[0].event_name ?? 0, 10);
    return Number.isNaN(mins) ? 0 : mins * 60;
};

// Derive the clock model from the clock events. Returns null (no clock / reset)
// or { base, refMs, running, frozenSeconds, limit }:
//   base          seconds at the period kick-off (0 for 1H, 2700 for 2H)
//   refMs         the period kick-off instant (server ms, from ref_clock)
//   running       still counting (no matching end event yet)
//   frozenSeconds seconds to display when stopped (end event present)
//   limit         max seconds for the period (period limit + additional time)
// The latest clock_overwrite for `period` that landed after `startMs` (i.e.
// during that period's live play) redefines the period's anchor exactly as if
// its own clock_start_1h/2h had been re-fired at that instant with a new
// ref_clock — the same mechanism the "override half" feature already uses.
// Only the *running* calc reads this; the end-of-half freeze below snaps to a
// fixed target (getPeriodEndSeconds) regardless of the anchor, so an earlier
// overwrite can't leave the frozen display stuck on a stale calculation.
const effectiveAnchor = (events, period, startMs, defaultBase) => {
    const overwrite = events
        .filter((e) => keyOf(e) === CLOCK_EVENT_KEYS.OVERWRITE && Number(e.game_period) === period)
        .map((e) => ({ e, ms: eventInstantMs(e) }))
        .filter(({ ms }) => ms > startMs)
        .sort((a, b) => b.ms - a.ms)[0];
    if (overwrite) {
        return { base: Number(overwrite.e.time_in_seconds) || 0, refMs: overwrite.ms };
    }
    return { base: defaultBase, refMs: startMs };
};

// Fixed period-end snap target (45:00 / 90:00) plus any announced added time —
// what the clock should freeze at when a half ends, regardless of how long it
// actually took to click End Half or whether the clock was overwritten
// earlier in the period.
export const getPeriodEndSeconds = (events, period, firstHalfEndSeconds, secondHalfEndSeconds) => {
    const base = (period === 2 ? secondHalfEndSeconds : firstHalfEndSeconds) ?? (period === 2 ? 5400 : 2700);
    return base + additionalSecondsForPeriod(events, period);
};

// Match is over when the latest clock_end_2h comes after every kick-off and
// reset. Read from the saved events, so it survives a page reload and is the
// same on every operator's screen (the in-memory `period` is neither).
export const isMatchEnded = (events) => {
    if (!Array.isArray(events)) return false;
    const latestMs = (key) =>
        events.reduce((max, e) => (keyOf(e) === key ? Math.max(max, eventInstantMs(e)) : max), -1);
    const end2 = latestMs(CLOCK_EVENT_KEYS.END_2H);
    if (end2 < 0) return false;
    return end2 > latestMs(CLOCK_EVENT_KEYS.START_1H)
        && end2 > latestMs(CLOCK_EVENT_KEYS.START_2H)
        && end2 > latestMs(CLOCK_EVENT_KEYS.RESET);
};

export const deriveClockModel = (
    events,
    firstHalfLimit,
    secondHalfLimit,
    firstHalfEndSeconds,
    secondHalfEndSeconds
) => {
    if (!Array.isArray(events) || events.length === 0) return null;

    const latestOf = (key) =>
        events
            .filter((e) => keyOf(e) === key)
            .sort((a, b) => eventInstantMs(b) - eventInstantMs(a))[0] || null;

    const reset = latestOf(CLOCK_EVENT_KEYS.RESET);
    const start1 = latestOf(CLOCK_EVENT_KEYS.START_1H);
    const end1 = latestOf(CLOCK_EVENT_KEYS.END_1H);
    const start2 = latestOf(CLOCK_EVENT_KEYS.START_2H);
    const end2 = latestOf(CLOCK_EVENT_KEYS.END_2H);
    const overwrite = latestOf(CLOCK_EVENT_KEYS.OVERWRITE);

    // Whichever period-defining event happened most recently wins.
    const defining = [reset, start1, end1, start2, end2, overwrite].filter(Boolean);
    if (defining.length === 0) return null;
    const latest = defining.sort((a, b) => eventInstantMs(b) - eventInstantMs(a))[0];
    if (keyOf(latest) === CLOCK_EVENT_KEYS.RESET) return null; // reset → 00:00, stopped

    const start1Ms = start1 ? eventInstantMs(start1) : -1;
    const start2Ms = start2 ? eventInstantMs(start2) : -1;

    // 2nd half if its kick-off is the most recent start.
    if (start2 && start2Ms >= start1Ms) {
        const { base, refMs } = effectiveAnchor(events, 2, start2Ms, 2700);
        const limit = secondHalfLimit + additionalSecondsForPeriod(events, 2);
        if (end2 && eventInstantMs(end2) > start2Ms) {
            // Half ended → snap to the fixed period-end target (+ announced
            // added time), not wherever the elapsed-time calc landed. This is
            // deliberately independent of base/refMs so an earlier overwrite
            // can't leave the frozen value stuck on a stale calculation.
            const frozen = Math.min(limit, getPeriodEndSeconds(events, 2, firstHalfEndSeconds, secondHalfEndSeconds));
            return { base, refMs, running: false, frozenSeconds: frozen, limit };
        }
        return { base, refMs, running: true, frozenSeconds: null, limit };
    }

    if (start1) {
        const { base, refMs } = effectiveAnchor(events, 1, start1Ms, 0);
        const limit = firstHalfLimit + additionalSecondsForPeriod(events, 1);
        if (end1 && eventInstantMs(end1) > start1Ms) {
            const frozen = Math.min(limit, getPeriodEndSeconds(events, 1, firstHalfEndSeconds, secondHalfEndSeconds));
            return { base, refMs, running: false, frozenSeconds: frozen, limit };
        }
        return { base, refMs, running: true, frozenSeconds: null, limit };
    }

    return null;
};

/**
 * OPUS-1172: event-sourced match clock.
 * @param clockEvents  the fixture's clock_* events (from useFixtureData)
 * @param getServerNow () => server-anchored now in ms (from useServerClock)
 * @param updateRenderer broker send — still drives the on-air graphic clock
 */
export const useClockManager = (clockEvents, getServerNow, updateRenderer, options = {}) => {
    const firstHalfLimit = options.firstHalfLimit ?? 2700;
    const secondHalfLimit = options.secondHalfLimit ?? 5400;
    const firstHalfEndSeconds = options.firstHalfEndSeconds ?? 2700;
    const secondHalfEndSeconds = options.secondHalfEndSeconds ?? 5400;

    const [clockTime, setClockTime] = useState("00:00");
    const [currentClockDirection, setCurrentClockDirection] = useState("UP");
    const [clockStatus, setClockStatus] = useState("STOP");
    const [clockLimit, setClockLimit] = useState(firstHalfLimit);

    // Interval handle for the 1Hz UI tick (owned solely by the event effect).
    const clockIntervalRef = useRef(null);

    const formatClockTime = (seconds) => {
        if (isNaN(seconds)) return "00:00";
        if (seconds < 0) return "00:00";
        const min = Math.floor((seconds / 60) % 3600).toString().padStart(2, "0");
        const sec = Math.floor(seconds % 60).toString().padStart(2, "0");
        return `${min}:${sec}`;
    };

    // Derive the clock state from the fixture's clock events. Re-derives only
    // when the events actually change — switching renderer no longer touches it
    // (OPUS-1172: the clock is fixture-scoped, not renderer/broker-scoped).
    const model = useMemo(
        () => deriveClockModel(clockEvents, firstHalfLimit, secondHalfLimit, firstHalfEndSeconds, secondHalfEndSeconds),
        [clockEvents, firstHalfLimit, secondHalfLimit, firstHalfEndSeconds, secondHalfEndSeconds]
    );

    // Exposed so callers (StatsPanel/TXLive) can compute the same period-end
    // snap value for the persisted clock_end_1h/2h event's time_in_seconds,
    // keeping the DDS timeline in sync with what's displayed here.
    const getEndSeconds = useCallback(
        (period) => getPeriodEndSeconds(clockEvents, period, firstHalfEndSeconds, secondHalfEndSeconds),
        [clockEvents, firstHalfEndSeconds, secondHalfEndSeconds]
    );

    // Tick the display from the model against server-anchored now. The interval
    // only advances the *rate*; absolute match time is recomputed every second
    // as base + (getServerNow() − refClock), so a wrong workstation clock or a
    // dropped tick can't skew it — and it self-corrects on every server resync.
    useEffect(() => {
        if (clockIntervalRef.current) {
            clearInterval(clockIntervalRef.current);
            clockIntervalRef.current = null;
        }

        // No clock yet, or a match reset → 00:00, stopped.
        if (!model) {
            setClockTime("00:00");
            setClockStatus("STOP");
            setClockLimit(firstHalfLimit);
            return;
        }

        const { base, refMs, running, frozenSeconds, limit } = model;
        setClockLimit(limit);

        // Half ended → freeze at the recorded time.
        if (!running) {
            setClockTime(formatClockTime(frozenSeconds));
            setClockStatus("STOP");
            return;
        }

        const compute = () => {
            const elapsed = Math.max(0, Math.floor((getServerNow() - refMs) / 1000));
            return Math.min(limit, base + elapsed);
        };

        const paint = () => {
            const secs = compute();
            setClockTime(formatClockTime(secs));
            return secs;
        };

        const initial = paint();
        if (initial >= limit) {
            // Already at/past the period limit (incl. announced stoppage).
            setClockStatus("STOP");
            return;
        }

        setClockStatus("START");
        clockIntervalRef.current = setInterval(() => {
            const secs = paint();
            if (secs >= limit) {
                clearInterval(clockIntervalRef.current);
                clockIntervalRef.current = null;
                setClockStatus("STOP");
            }
        }, 1000);

        return () => {
            if (clockIntervalRef.current) {
                clearInterval(clockIntervalRef.current);
                clockIntervalRef.current = null;
            }
        };
        // Depend on the model's primitive fields, not its object identity, so a
        // non-clock event push (goal, sub, …) re-deriving an equivalent model
        // doesn't tear down + rebuild the running interval.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model?.base, model?.refMs, model?.running, model?.frozenSeconds, model?.limit, getServerNow, firstHalfLimit]);

    const handleClockAction = async (action, startTime = null, direction = null, rendererId) => {
        const clockName = "Clock0"; // Should probably be configurable or match what is received

        const clockPayload = {
            renderer: rendererId,
            layer: "VizGlobal",
            caption: {
                id: "VizGlobal",
                action: "",
                data: {
                    VizGlobal: {
                        Clocks: {}
                    }
                }
            }
        };

        let newDirection = direction || currentClockDirection;

        if (action === "START") {
            let iClockTime = 0;
            if (startTime) {
                const timeParts = startTime.split(':');
                if (timeParts.length === 2) {
                    const minutes = parseInt(timeParts[0], 10) || 0;
                    const seconds = parseInt(timeParts[1], 10) || 0;
                    iClockTime = minutes * 60 + seconds;
                }
            }

            let limit = String(firstHalfLimit);
            if (iClockTime > firstHalfLimit) limit = String(secondHalfLimit);

            clockPayload.caption.action = `${clockName.toUpperCase()}_START`;
            clockPayload.caption.data.VizGlobal.Clocks[clockName] = {
                iClockTime: String(iClockTime),
                iClockLimit: limit,
                strClockDirection: newDirection
            };
            // OPUS-1172: the UI clock no longer reacts to this action — it ticks
            // from the clock_start_1h/2h events. This only drives the on-air
            // broker graphic. Same for the STOP / RESET branches below.

        } else if (action === "START_2ND_HALF") {
            clockPayload.caption.action = `${clockName.toUpperCase()}_START`;
            clockPayload.caption.data.VizGlobal.Clocks[clockName] = {
                iClockTime: "2700",
                iClockLimit: String(secondHalfLimit),
                strClockDirection: "UP"
            };
            newDirection = "UP";
        } else if (action === "STOP") {
            // Plain stop (no half boundary) just freezes the renderer's
            // clock where it is.
            clockPayload.caption.action = `${clockName.toUpperCase()}_STOP`;
            delete clockPayload.caption.data;
        } else if (action === "STOP_1ST_HALF" || action === "STOP_2ND_HALF") {
            // End-of-half snaps the on-air clock to the fixed period-end
            // target (45:00 / 90:00) plus any announced added time — not
            // wherever the clock happened to be when End Half was clicked.
            // Uses CLOCK0_START with iClockTime === iClockLimit, which the
            // renderer treats as a frozen display at that value.
            const period = action === "STOP_1ST_HALF" ? 1 : 2;
            const endSeconds = getPeriodEndSeconds(clockEvents, period, firstHalfEndSeconds, secondHalfEndSeconds);
            clockPayload.caption.action = `${clockName.toUpperCase()}_START`;
            clockPayload.caption.data.VizGlobal.Clocks[clockName] = {
                iClockTime: String(endSeconds),
                iClockLimit: String(endSeconds),
                strClockDirection: newDirection
            };
        } else if (action === "SET_TO_ZERO") {
            clockPayload.caption.action = `${clockName.toUpperCase()}_START`;
            clockPayload.caption.data.VizGlobal.Clocks[clockName] = {
                iClockTime: "0",
                iClockLimit: "2700",
                strClockDirection: newDirection
            };
        } else if (action === "REVERSE") {
            newDirection = currentClockDirection === "UP" ? "DOWN" : "UP";
            clockPayload.caption.action = `${clockName.toUpperCase()}_START`;
            clockPayload.caption.data.VizGlobal.Clocks[clockName] = {
                iClockTime: "0",
                iClockLimit: "2700",
                strClockDirection: newDirection
            };
        } else if (action === "RESET") {
            // Use the standard CLOCK0_START action with a 0/0 state — the
            // renderer treats iClockTime === iClockLimit as a frozen
            // display. RESET needs to snap the clock back to 00:00, so
            // unlike the half-end STOP variants (which freeze in place),
            // we DO want to push a specific time. caption.action = ""
            // wasn't recognized by the renderer — that's why reset never
            // actually stopped the on-air clock before this fix.
            clockPayload.caption.action = `${clockName.toUpperCase()}_START`;
            clockPayload.caption.data.VizGlobal.Clocks[clockName] = {
                iClockTime: "0",
                iClockLimit: "0",
                strClockDirection: "UP"
            };
            newDirection = "UP";
            // OPUS-1172: the UI clock resets when the clock_match_reset event
            // lands (deriveClockModel → null → 00:00/STOP). No local mutation
            // here — this branch only snaps the on-air broker graphic.
        }

        setCurrentClockDirection(newDirection);

        try {
            await updateRenderer(clockPayload);

        } catch (e) {
            console.error("Failed to send clock action", e);
        }
    };

    return {
        clockTime,
        currentClockDirection,
        clockStatus,
        clockLimit,
        getEndSeconds,
        handleClockAction,
        formatClockTime
    };
};
